import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/utils/api";
import { TEST_IDS } from "@/constants/testIds";
import { Eye, EyeOff, Target, AlertCircle, CheckCircle } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const registered = searchParams.get("registered") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080C14] flex flex-col items-center justify-center px-4">
      {/* Background gradient */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md animate-fade-in-up">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-500/15 border border-emerald-500/30 rounded-2xl mb-4">
            <Target className="w-7 h-7 text-emerald-400" />
          </div>
          <h1 className="font-outfit text-3xl font-bold text-slate-50 tracking-tight">
            RadarLit
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Tu radar en la contratación pública · SECOP II
          </p>
          <p className="text-emerald-400 text-xs mt-1 font-medium">
            Inteligencia para contratar con el Estado
          </p>
        </div>

        {/* Form card */}
        <div className="bg-slate-900 border border-slate-700/50 rounded-2xl p-8 shadow-2xl">
          <h2 className="text-slate-200 font-semibold text-lg mb-6">Iniciar Sesión</h2>

          {registered && (
            <div className="flex items-start gap-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm rounded-lg px-4 py-3 mb-5">
              <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Cuenta creada. Inicia sesión para comenzar tu prueba gratuita.</span>
            </div>
          )}
          {error && (
            <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg px-4 py-3 mb-5">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-slate-400 text-xs font-medium mb-1.5 block">
                Correo electrónico
              </label>
              <input
                data-testid={TEST_IDS.LOGIN_EMAIL}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full bg-slate-800 border border-slate-700 text-slate-100 rounded-lg px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all"
                placeholder="tu@email.com"
              />
            </div>

            <div>
              <label className="text-slate-400 text-xs font-medium mb-1.5 block">
                Contraseña
              </label>
              <div className="relative">
                <input
                  data-testid={TEST_IDS.LOGIN_PASSWORD}
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full bg-slate-800 border border-slate-700 text-slate-100 rounded-lg px-4 py-2.5 pr-10 text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              data-testid={TEST_IDS.LOGIN_SUBMIT}
              type="submit"
              disabled={loading}
              className="w-full btn-primary rounded-lg py-2.5 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {loading ? "Verificando..." : "Ingresar al Sistema"}
            </button>
          </form>

          <div className="mt-4 flex items-center justify-between">
            <Link
              data-testid={TEST_IDS.LOGIN_FORGOT}
              to="/forgot-password"
              className="text-slate-500 hover:text-emerald-400 text-xs transition-colors"
            >
              ¿Olvidaste tu contraseña?
            </Link>
            <Link to="/register" className="text-emerald-400 hover:text-emerald-300 text-xs transition-colors font-medium">
              Crear cuenta →
            </Link>
          </div>
        </div>

        <p className="text-center text-slate-600 text-xs mt-6">
          RadarLit © 2025 · by Innova App Solutions
        </p>
      </div>
    </div>
  );
}
